<template>
  <o-card>
    <template slot="header">
      <h5 class="mb-0">{{ $t('sandoz.items') }}</h5>
    </template>

    <p v-if="!deliveryAddresses.length" class="text-muted">{{ $t('sandoz.no_addresses_for_items') }}</p>

    <b-tabs v-else>
      <b-tab v-for="(addr, addrIdx) in deliveryAddresses"
             :key="addr.id || addrIdx"
             :title="addressTitle(addr)">
        <p class="text-muted small" v-if="addr.address_type === 'master'">{{ $t('sandoz.items_master_address_note') }}</p>

        <table v-if="addr.items && addr.items.length" class="table table-sm align-middle">
          <thead>
            <tr>
              <th></th>
              <th>{{ $t('sandoz.product') }}</th>
              <th>{{ $t('sandoz.valid_from') }}</th>
              <th>{{ $t('sandoz.valid_to') }}</th>
              <th class="text-right">{{ $t('sandoz.yearly_quantity') }}</th>
              <th class="text-right">Q1</th>
              <th class="text-right">Q2</th>
              <th class="text-right">Q3</th>
              <th class="text-right">Q4</th>
              <th class="text-right">{{ $t('sandoz.price') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(line, lineIdx) in addr.items">
              <tr :key="lineIdx">
                <td>
                  <b-btn v-if="hasChildren(line)" size="sm" variant="link" @click="toggleExpand(addrIdx, lineIdx)">
                    <fa :icon="isExpanded(addrIdx, lineIdx) ? 'chevron-down' : 'chevron-right'"/>
                  </b-btn>
                </td>
                <td>{{ line.product_name || (line.product && line.product.name) }}</td>
                <td>{{ line.valid_from }}</td>
                <td>{{ line.valid_to }}</td>
                <td class="text-right">
                  <b-form-input v-if="!readonly && !isReadonlyQty(line, 'yearly')"
                                v-model.number="line.delivery.yearly.quantity"
                                type="number" size="sm" class="text-right"/>
                  <span v-else>{{ line.delivery && line.delivery.yearly && line.delivery.yearly.quantity }}</span>
                </td>
                <td v-for="q in ['q1','q2','q3','q4']" :key="q" class="text-right">
                  <b-form-input v-if="!readonly && line.delivery && line.delivery[q]"
                                v-model.number="line.delivery[q].quantity"
                                type="number" size="sm" class="text-right"/>
                </td>
                <td class="text-right">
                  <b-form-input v-if="!readonly"
                                v-model.number="line.price"
                                type="number" step="0.01" size="sm" class="text-right"/>
                  <span v-else>{{ line.price }}</span>
                </td>
              </tr>
              <template v-if="hasChildren(line) && isExpanded(addrIdx, lineIdx)">
                <tr v-for="(child, childIdx) in line.children" :key="lineIdx + '-c-' + childIdx" class="table-light">
                  <td></td>
                  <td><span class="pl-3">{{ child.product_name || (child.product && child.product.name) }}</span></td>
                  <td>{{ child.valid_from }}</td>
                  <td>{{ child.valid_to }}</td>
                  <td class="text-right">
                    <b-form-input v-if="!readonly"
                                  v-model.number="child.delivery.yearly.quantity"
                                  type="number" size="sm" class="text-right"/>
                  </td>
                  <td v-for="q in ['q1','q2','q3','q4']" :key="q" class="text-right">
                    <b-form-input v-if="!readonly && child.delivery && child.delivery[q]"
                                  v-model.number="child.delivery[q].quantity"
                                  type="number" size="sm" class="text-right"/>
                  </td>
                  <td class="text-right">
                    <b-form-input v-if="!readonly" v-model.number="child.price" type="number" step="0.01" size="sm" class="text-right"/>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>

        <p v-else class="text-muted">{{ $t('sandoz.no_items_for_address') }}</p>

        <h6 v-if="addr.adhoc_items && addr.adhoc_items.length" class="mt-3">{{ $t('sandoz.adhoc_items') }}</h6>
        <table v-if="addr.adhoc_items && addr.adhoc_items.length" class="table table-sm">
          <thead>
            <tr>
              <th>{{ $t('sandoz.product') }}</th>
              <th>{{ $t('sandoz.valid_from') }}</th>
              <th>{{ $t('sandoz.valid_to') }}</th>
              <th class="text-right">{{ $t('sandoz.price') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="adhoc in addr.adhoc_items" :key="adhoc.id">
              <td>{{ adhoc.product_name || (adhoc.product && adhoc.product.name) }}</td>
              <td>{{ adhoc.valid_from }}</td>
              <td>{{ adhoc.valid_to }}</td>
              <td class="text-right">{{ adhoc.price }}</td>
            </tr>
          </tbody>
        </table>
      </b-tab>
    </b-tabs>

    <p class="text-muted small mt-3">{{ $t('sandoz.items_phase4_note') }}</p>
  </o-card>
</template>

<script>
import sectionMixin from '../sectionMixin'

/**
 * Contract lines with product picker. Source: gadget/ContractDetail.vue (most reusable line UI).
 *
 * First-cut shape: address-tabbed table that lists products with yearly + quarterly quantities,
 * price, and validity. Adhoc-item handling (gadget-specific) is kept as a sub-section.
 *
 * The product picker / addItemsToAddress flow and the parent-child quantity cascade live in the
 * legacy gadget page and aren't ported yet — sections opening this in the dynamic page see the
 * existing data but cannot add new items until the cutover. This matches Phase 4's "structural
 * first cut; full parity validated during Phase 8" expectation.
 */
export default {
  name: 'SectionItems',
  mixins: [sectionMixin],
  data() {
    return {
      expanded: {} // key: "addrIdx-lineIdx" -> bool
    }
  },
  computed: {
    deliveryAddresses() {
      const addresses = Array.isArray(this.item.addresses) ? this.item.addresses : []
      // Educational uses master; everything else uses delivery. Match gadget behaviour: delivery only.
      const t = this.item.type || ''
      if (t.endsWith('_EDU')) {
        return addresses.filter(a => a.address_type === 'master')
      }
      return addresses.filter(a => a.address_type === 'delivery' || a.address_type === 'master')
    }
  },
  methods: {
    addressTitle(addr) {
      return [addr.company_name, addr.city].filter(Boolean).join(', ') || addr.customer_external_id || '-'
    },
    hasChildren(line) {
      return Array.isArray(line.children) ? line.children.length > 0
        : !!(line.children && Object.keys(line.children).length)
    },
    toggleExpand(addrIdx, lineIdx) {
      const key = `${addrIdx}-${lineIdx}`
      this.$set(this.expanded, key, !this.expanded[key])
    },
    isExpanded(addrIdx, lineIdx) {
      return !!this.expanded[`${addrIdx}-${lineIdx}`]
    },
    isReadonlyQty(line, slot) {
      // Mirrors the legacy isReadonly(line, slot) heuristic at a coarse level — refined in the cutover.
      if (this.readonly) {
        return true
      }
      const v = line.user_date_yearly_input
      return slot === 'yearly' ? !v : false
    }
  }
}
</script>
